import React, { useEffect, useRef, useState } from 'react';
import { Search, ChevronLeft, ChevronRight, ListChecks } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import { horaConSegundos, hora, fechaCorta, hoyISO } from './formato';
import { Avatar, NombrePersona } from './ui';

const POR_PAGINA = 100;
// Marcas que no son de un empleado; se ven solo con "Incluir marcas sin persona"
const SIN_PERSONA = { 0: 'Rostro no reconocido', 99999999: 'Usuario genérico del aparato' };
const REFRESCO_MS = 10000;
// Un marcaje que llegó más de 5 min después de hecho venía guardado en el aparato
const MARGEN_REENVIO_MS = 5 * 60 * 1000;

const NotaLlegada = ({ m }) => {
    if (m.origen === 'memoria') {
        return <p className="text-xs text-slate-400 mt-0.5">traído de la memoria del aparato el {fechaCorta(m.recibido_en)}</p>;
    }
    const notas = [];
    const demora = new Date(m.recibido_en) - new Date(m.fecha_hora);
    if (demora > MARGEN_REENVIO_MS) {
        notas.push(`llegó ${fechaCorta(m.recibido_en)} a las ${hora(m.recibido_en)}`);
    }
    if (m.veces_recibido > 1) notas.push(`recibido ${m.veces_recibido} veces`);
    if (notas.length === 0) return null;
    return <p className="text-xs text-slate-400 mt-0.5">{notas.join(', ')}</p>;
};

const MarcajesPanel = ({ aparatos }) => {
    const [desde, setDesde] = useState(hoyISO());
    const [hasta, setHasta] = useState(hoyISO());
    const [aparatoId, setAparatoId] = useState('');
    const [texto, setTexto] = useState('');
    const [busqueda, setBusqueda] = useState('');
    const [sinPersona, setSinPersona] = useState(false);
    const [pagina, setPagina] = useState(1);
    const [datos, setDatos] = useState(null);
    const [error, setError] = useState(null);
    const [nuevos, setNuevos] = useState(new Set());
    const vistos = useRef(null);

    // La búsqueda espera a que se deje de escribir
    useEffect(() => {
        const t = setTimeout(() => setBusqueda(texto.trim()), 300);
        return () => clearTimeout(t);
    }, [texto]);

    // Al cambiar filtros se vuelve a la primera página y no se resalta nada
    useEffect(() => {
        setPagina(1);
        vistos.current = null;
    }, [desde, hasta, aparatoId, busqueda, sinPersona]);

    const incluyeHoy = hasta >= hoyISO();

    useEffect(() => {
        let vivo = true;
        const cargar = async () => {
            try {
                const { data } = await axiosInstance.get('/api/asistencia/marcajes', {
                    params: {
                        desde, hasta, page: pagina, per_page: POR_PAGINA,
                        aparato_id: aparatoId || undefined,
                        q: busqueda || undefined,
                        sin_persona: sinPersona ? 1 : undefined,
                    },
                });
                if (!vivo) return;
                // Resalta lo que llegó desde la carga anterior (no en la primera)
                const ids = new Set(data.marcajes.map((m) => m.id));
                if (vistos.current) {
                    setNuevos(new Set([...ids].filter((id) => !vistos.current.has(id))));
                } else {
                    setNuevos(new Set());
                }
                vistos.current = ids;
                setDatos(data);
                setError(null);
            } catch (e) {
                if (vivo) setError('No se pudieron cargar los marcajes. Se reintenta solo en unos segundos.');
            }
        };
        cargar();
        const intervalo = incluyeHoy && pagina === 1 ? setInterval(cargar, REFRESCO_MS) : null;
        return () => {
            vivo = false;
            if (intervalo) clearInterval(intervalo);
        };
    }, [desde, hasta, aparatoId, busqueda, sinPersona, pagina, incluyeHoy]);

    const totalPaginas = datos ? Math.max(1, Math.ceil(datos.total / POR_PAGINA)) : 1;
    const variosDias = desde !== hasta;

    return (
        <div className="space-y-4">
            {/* Filtros */}
            <div className="flex flex-wrap items-end gap-3">
                <label className="flex flex-col gap-1">
                    <span className="text-xs text-slate-500">Desde</span>
                    <input
                        type="date" value={desde} max={hasta}
                        onChange={(e) => e.target.value && setDesde(e.target.value)}
                        className="h-9 px-3 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-400/50"
                    />
                </label>
                <label className="flex flex-col gap-1">
                    <span className="text-xs text-slate-500">Hasta</span>
                    <input
                        type="date" value={hasta} min={desde}
                        onChange={(e) => e.target.value && setHasta(e.target.value)}
                        className="h-9 px-3 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-400/50"
                    />
                </label>
                <label className="flex flex-col gap-1">
                    <span className="text-xs text-slate-500">Aparato</span>
                    <select
                        value={aparatoId} onChange={(e) => setAparatoId(e.target.value)}
                        className="h-9 px-3 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-400/50"
                    >
                        <option value="">Todos</option>
                        {aparatos.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
                    </select>
                </label>
                <label className="flex flex-col gap-1 flex-1 min-w-[12rem]">
                    <span className="text-xs text-slate-500">Persona</span>
                    <div className="relative">
                        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            type="search" value={texto} onChange={(e) => setTexto(e.target.value)}
                            placeholder="Nombre o cédula"
                            className="h-9 w-full pl-9 pr-3 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/50"
                        />
                    </div>
                </label>
                <label className="h-9 flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
                    <input
                        type="checkbox" checked={sinPersona} onChange={(e) => setSinPersona(e.target.checked)}
                        className="rounded border-slate-300 text-cyan-500 focus:ring-cyan-400/50"
                    />
                    Incluir marcas sin persona
                </label>
            </div>

            {/* Resumen */}
            {datos && (
                <p className="text-sm text-slate-500">
                    <span className="font-medium text-slate-900 tabular-nums">{datos.total}</span> marcajes de{' '}
                    <span className="font-medium text-slate-900 tabular-nums">{datos.personas}</span> personas
                    {incluyeHoy && pagina === 1 && <span className="text-slate-400">. Se actualiza cada 10 segundos.</span>}
                </p>
            )}
            {error && <p className="text-sm text-rose-600">{error}</p>}

            {/* Tabla */}
            <div className="bg-white rounded-2xl border border-slate-200/80 overflow-hidden">
                {datos && datos.marcajes.length === 0 ? (
                    <div className="p-12 text-center">
                        <ListChecks size={36} className="mx-auto text-slate-300 mb-3" />
                        <p className="text-slate-600 font-medium">No hay marcajes con estos filtros</p>
                        <p className="text-slate-400 text-sm mt-1">
                            {incluyeHoy ? 'Los nuevos aparecen aquí apenas alguien marca' : 'Prueba con otras fechas o quita filtros'}
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                                    <th className="px-5 py-3 font-medium">{variosDias ? 'Fecha y hora' : 'Hora'}</th>
                                    <th className="px-5 py-3 font-medium">Persona</th>
                                    <th className="px-5 py-3 font-medium">Aparato</th>
                                    <th className="px-5 py-3 font-medium">Cómo marcó</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {(datos?.marcajes || []).map((m) => (
                                    <tr
                                        key={m.id}
                                        className={`align-top transition-colors duration-[2000ms] ${nuevos.has(m.id) ? 'bg-cyan-50' : 'bg-white'}`}
                                    >
                                        <td className="px-5 py-3 whitespace-nowrap tabular-nums text-slate-700">
                                            {variosDias && <span className="text-slate-400">{fechaCorta(m.fecha_hora)} </span>}
                                            {horaConSegundos(m.fecha_hora)}
                                        </td>
                                        <td className="px-5 py-3 min-w-[14rem]">
                                            <div className="flex items-start gap-3">
                                                <Avatar persona={m.persona || { nombre: '?' }} size={32} />
                                                <div className="min-w-0">
                                                    {m.persona ? (
                                                        <NombrePersona persona={m.persona} className="font-medium text-slate-900" />
                                                    ) : (
                                                        <p className="text-slate-500">
                                                            {SIN_PERSONA[m.enroll_id] || 'No está en Centro SURA'}
                                                        </p>
                                                    )}
                                                    {m.enroll_id !== '0' && (
                                                        <p className="text-xs text-slate-400 tabular-nums">C.I. {m.enroll_id}</p>
                                                    )}
                                                    <NotaLlegada m={m} />
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-5 py-3 whitespace-nowrap text-slate-600">{m.aparato.nombre}</td>
                                        <td className="px-5 py-3 whitespace-nowrap text-slate-600">
                                            {m.modo || (m.enroll_id === '0' || m.modo_codigo == null ? '' : `Código ${m.modo_codigo}`)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Paginación */}
            {datos && totalPaginas > 1 && (
                <div className="flex items-center justify-end gap-2 text-sm text-slate-600">
                    <span className="tabular-nums">Página {pagina} de {totalPaginas}</span>
                    <button
                        onClick={() => setPagina((p) => Math.max(1, p - 1))} disabled={pagina === 1}
                        className="p-1.5 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60"
                        aria-label="Página anterior"
                    >
                        <ChevronLeft size={16} />
                    </button>
                    <button
                        onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))} disabled={pagina === totalPaginas}
                        className="p-1.5 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60"
                        aria-label="Página siguiente"
                    >
                        <ChevronRight size={16} />
                    </button>
                </div>
            )}
        </div>
    );
};

export default MarcajesPanel;
